// The app-header tagline beside the logo. Static chrome goes invisible after a day or two, so the
// line is made contextual: each view carries a small pool of its own lines and one is picked on
// navigation. The change is user-caused (they clicked the nav item), so it reads as responsive
// rather than as the restless motion a self-looping ticker would add to fixed chrome.
//
// Views with no pool of their own fall back to DEFAULT_LINES — so a new diagnostic page works on
// day one, and only needs an entry here when it deserves its own voice.

/** Three lines per view — enough variety to stay alive, few enough to stay curated. */
type Pool = readonly [string, string, string];

/** Brand lines: Home, everything below the nav divider, and any view without its own pool. */
export const DEFAULT_LINES: Pool = [
  'Cross-methodology Coaching',
  'Design what good looks like',
  'A second pair of eyes',
];

/** Per-view pools, keyed by the nav item's `data-view`. */
export const TAGLINES: Readonly<Record<string, Pool>> = {
  diagnostic: [
    'Not a summary. A diagnosis.',
    'Every skill, scored on evidence',
    'What happened, and why it scored',
  ],
  'cross-skill': [
    "Skills don't fail alone",
    'Find the domino, not the symptom',
    'What lifted it, what dragged it',
  ],
  'buyer-side': [
    'The other half of the call',
    'Buying signals, not selling ones',
    'Read the room, not just the rep',
  ],
  meddpicc: [
    'Is this deal actually qualified?',
    'Metrics to Champion, on evidence',
    'The qualification, not the pitch',
  ],
  'bespoke-playbook': [
    'What your A-players do differently',
    'Top performers vs the core team',
    'Turn the gap into a playbook',
  ],
  'cro-dash': [
    'The team, not the transcript',
    'Zoom out to the whole floor',
    'Where coaching time pays back',
  ],
};

/**
 * Pick a line for `view`, avoiding `current` so navigating back to a page never appears to do
 * nothing. `rand` is injectable for tests; it must return [0, 1).
 */
export function pickTagline(view: string, current?: string | null, rand: () => number = Math.random): string {
  const pool = TAGLINES[view] ?? DEFAULT_LINES;
  const choices = pool.length > 1 ? pool.filter((l) => l !== current) : pool;
  const i = Math.min(choices.length - 1, Math.floor(rand() * choices.length));
  return choices[i] ?? DEFAULT_LINES[0];
}

const FADE_MS = 400;
let fadeTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Swap the header tagline to a line for `view`. The first call (page load) sets the text outright;
 * later calls cross-fade, unless the user asked for reduced motion.
 */
export function setTagline(view: string, opts: { animate?: boolean } = {}): void {
  const el = document.querySelector<HTMLElement>('.app-header-tagline');
  if (!el) return;

  const next = pickTagline(view, el.textContent?.trim());
  if (next === el.textContent?.trim()) return;

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (!opts.animate || reduced) {
    el.textContent = next;
    return;
  }

  clearTimeout(fadeTimer);
  el.classList.add('is-fading');
  fadeTimer = setTimeout(() => {
    el.textContent = next;
    el.classList.remove('is-fading');
  }, FADE_MS / 2);
}
